import { useCallback, useEffect, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Alert } from '@factorialco/f0-react'
import { DynamicTeamSpecificationDocument } from 'generated/resources/graphql'
import { useDebounceValue, useMediaQuery } from 'usehooks-ts'

import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { SpecificationSelector } from 'modules/posts/components/DynamicTeams/SpecificationSelector'
import { EmployeePreviewPanel } from 'modules/posts/components/Groups/DynamicGroupsAudience/EmployeePreviewPanel'

type Props = {
  dynamicTeamId?: string | null
  onSpecificationLoaded: (specification: string | null) => void
  onSpecificationChange: (specification: string) => void
  onEvaluationError: (hasError: boolean) => void
  disabled?: boolean
  error?: string | null
}

const DEBOUNCE_DELAY_MS = 300

function AutomaticInvitesView({
  dynamicTeamId,
  onSpecificationLoaded,
  onSpecificationChange,
  onEvaluationError,
  disabled,
  error,
}: Props) {
  const isWideScreen = useMediaQuery('(min-width: 800px)')

  const [currentSpecification, setCurrentSpecification] = useState<string | null>(null)
  const [pendingSpecification, setPendingSpecification] = useState<string | null>(null)
  const [cannotDisplaySpec, setCannotDisplaySpec] = useState(false)

  // Fetch dynamic team specification when component mounts (only if dynamicTeamId exists)
  const { data: dynamicTeamData, loading: dynamicTeamLoading } = useQuery(
    DynamicTeamSpecificationDocument,
    {
      variables: {
        ids: dynamicTeamId ? [dynamicTeamId] : [],
        first: 1,
        offsetPage: 1,
      },
      skip: !dynamicTeamId,
      fetchPolicy: 'network-only',
    }
  )

  // Update specification state when dynamic team data is fetched
  useEffect(() => {
    if (!dynamicTeamId) {
      setCurrentSpecification(null)
      setPendingSpecification(null)
      onSpecificationLoaded(null)
      return
    }

    const dynamicTeamEdges = dynamicTeamData?.posts?.dynamicTeamsConnection?.edges
    if (!dynamicTeamEdges?.[0]?.node) {
      return
    }

    const dynamicTeam = dynamicTeamEdges[0].node
    const specJson = dynamicTeam.specification ?? null

    setCurrentSpecification(specJson)
    setPendingSpecification(null)
    onSpecificationLoaded(specJson)
  }, [dynamicTeamId, dynamicTeamData])

  const specification = pendingSpecification ?? currentSpecification

  // Debounce the specification for the preview to avoid excessive API calls
  const [debouncedSpecification] = useDebounceValue(specification, DEBOUNCE_DELAY_MS)

  const handleSpecificationChange = useCallback(
    (newSpecification: string) => {
      setPendingSpecification(newSpecification)
      onSpecificationChange(newSpecification)
    },
    [onSpecificationChange]
  )

  const handleTransformError = useCallback((canDisplay: boolean) => {
    setCannotDisplaySpec(!canDisplay)
  }, [])

  const handlePreviewError = useCallback(
    (hasError: boolean) => {
      onEvaluationError(hasError)
    },
    [onEvaluationError]
  )

  if (dynamicTeamLoading) {
    return null
  }

  return (
    <>
      {error && (
        <Box paddingBottom="s16">
          <F0Alert variant="critical" title={error} description="" />
        </Box>
      )}
      {cannotDisplaySpec && (
        <Box paddingBottom="s16">
          <F0Alert
            variant="warning"
            title={i18n.t(
              'posts.communities.automatic_invites.errors.cannot_display_specification'
            )}
            description={i18n.t('posts.communities.automatic_invites.errors.create_new_definition')}
          />
        </Box>
      )}
      <Box flexDirection={isWideScreen ? 'row' : 'column'} gap="s16" width="full">
        <Box style={isWideScreen ? { flex: '65' } : undefined} width="full">
          <SpecificationSelector
            specification={specification}
            onChange={handleSpecificationChange}
            onTransformError={handleTransformError}
            disabled={disabled}
            enabledFilters={['location', 'direct_reports_of']}
          />
        </Box>
        <Box
          style={
            isWideScreen
              ? { flex: '35', borderLeft: '1px solid var(--f1-color-border-secondary)' }
              : { borderTop: '1px solid var(--f1-color-border-secondary)' }
          }
          paddingLeft={isWideScreen ? 's16' : undefined}
          paddingTop={isWideScreen ? undefined : 's16'}
          width="full"
        >
          <EmployeePreviewPanel
            specification={debouncedSpecification}
            onError={handlePreviewError}
          />
        </Box>
      </Box>
    </>
  )
}

export default AutomaticInvitesView
